import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Users, Star, Filter, Search, Play, Calendar } from 'lucide-react';
import { coursesAPI } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

interface Course {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail?: string;
  image?: string;
  instructor: {
    _id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    photo?: string;
  };
  level: string;
  duration: number;
  enrolledCount: number;
  rating: number;
  isFree: boolean;
  price: number;
  category?: string;
  tags?: string[];
}

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', level: '', search: '' });

  useEffect(() => {
    loadCourses();
  }, [filters]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await coursesAPI.getCourses(filters);
      setCourses(response.data.data || []);
    } catch (error: any) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInstructorName = (instructor: Course['instructor']) => {
    if (instructor?.firstName && instructor?.lastName) return `${instructor.firstName} ${instructor.lastName}`;
    return instructor?.name || 'Instructor';
  };

  const getLevelLabel = (level: string) => {
    const labels: Record<string, string> = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
    return labels[level] || level;
  };

  const getThumbnailUrl = (course: Course) => {
    const path = course.thumbnail || course.image;
    return path ? (path.startsWith('http') ? path : getImageUrl(path)) : null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">E-Learning Courses</h1>
          <p className="text-gray-600">Discover our online courses and develop your skills</p>
        </div>
        <Link to="/courses/calendar" className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          <Calendar className="h-5 w-5" />
          View Calendar
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for a course..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <select value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
            <option value="">All Categories</option>
            <option value="Development">Development</option>
            <option value="Technology">Technology</option>
            <option value="Business">Business</option>
            <option value="Agriculture">Agriculture</option>
            <option value="Others">Others</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-600">Try adjusting your search filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const thumbUrl = getThumbnailUrl(course);
            return (
              <Link key={course._id} to={`/courses/${course._id}`} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                {thumbUrl ? (
                  <img src={thumbUrl} alt={course.title} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-white opacity-50" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800 mb-2">{getLevelLabel(course.level)}</span>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 line-clamp-2">{course.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">{course.shortDescription || course.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{course.duration} min</span>
                    <span className="flex items-center"><Users className="h-4 w-4 mr-1" />{course.enrolledCount}</span>
                    <span className="flex items-center"><Star className="h-4 w-4 mr-1 text-yellow-500 fill-yellow-500" />{course.rating > 0 ? course.rating.toFixed(1) : 'New'}</span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-sm text-gray-600">{getInstructorName(course.instructor)}</span>
                    <span className="text-lg font-bold text-green-600">{course.isFree ? 'Free' : `$${course.price}`}</span>
                  </div>
                  <button className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2">
                    <Play className="h-4 w-4" />View Course
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Courses;
